import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import axios from "axios";
import zxcvbn from "zxcvbn";

import AuthLayout, { authField, AuthLabel } from "../../components/auth/AuthLayout";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { paths } from "../../../routes/paths";
import { baseURL } from "../../../config";
import tokens from "../../theme/tokens";

const { color, font, radius } = tokens;

const STRENGTH = [
  { label: "Very weak", color: color.danger },
  { label: "Weak", color: color.danger },
  { label: "Fair", color: color.warning },
  { label: "Strong", color: color.success },
  { label: "Very strong", color: color.success },
];

const ResetPwd = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [strength, setStrength] = useState(0);
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // The reset link carries the token as a query param.
  useEffect(() => {
    setResetToken(searchParams.get("token") || "");
  }, [searchParams]);

  const validate = () => {
    const next = {};
    if (!password) next.password = "Choose a new password.";
    if (password !== confirm) next.confirm = "Passwords don't match.";
    if (!resetToken) next.form = "This reset link is invalid or has expired.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await axios.post(`${baseURL}resetPassword/`, {
        reset_token: resetToken,
        new_password: password,
        confirm_new_password: confirm,
      });
      dispatch(
        showAppAlert({
          show: true,
          alertType: "success",
          alertMessage: "Password updated. Sign in with your new password.",
        })
      );
      navigate(paths.v1.signin);
    } catch (err) {
      const message = err.response?.data?.message || "Couldn't reset your password.";
      setErrors({ form: message });
      dispatch(showAppAlert({ show: true, alertType: "error", alertMessage: message }));
    } finally {
      setLoading(false);
    }
  };

  const meter = STRENGTH[strength];

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="Choose something you haven't used before."
      footer={
        <>
          Remembered it?{" "}
          <Box
            component={Link}
            to={paths.v1.signin}
            sx={{
              font: `700 12.5px ${font.sans}`,
              color: color.indigo,
              textDecoration: "none",
            }}
          >
            Sign in
          </Box>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <AuthLabel>New password</AuthLabel>
        <TextField
          fullWidth
          type={show ? "text" : "password"}
          placeholder="••••••••"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setStrength(e.target.value ? zxcvbn(e.target.value).score : 0);
            setErrors((x) => ({ ...x, password: "", form: "" }));
          }}
          error={Boolean(errors.password)}
          helperText={errors.password}
          sx={{ ...authField, mb: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" edge="end" onClick={() => setShow((s) => !s)}>
                  {show ? (
                    <IconEyeOff size={16} stroke={1.75} color={color.textMuted} />
                  ) : (
                    <IconEye size={16} stroke={1.75} color={color.textMuted} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {password && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
            <Box
              sx={{
                flex: 1,
                height: 4,
                borderRadius: `${radius.chip}px`,
                bgcolor: color.borderRow,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: 4,
                  width: `${((strength + 1) / 5) * 100}%`,
                  bgcolor: meter.color,
                  transition: "width 120ms ease",
                }}
              />
            </Box>
            <Box sx={{ font: `400 11px ${font.mono}`, color: meter.color }}>{meter.label}</Box>
          </Box>
        )}

        <AuthLabel>Confirm password</AuthLabel>
        <TextField
          fullWidth
          type={show ? "text" : "password"}
          placeholder="••••••••"
          value={confirm}
          onChange={(e) => {
            setConfirm(e.target.value);
            setErrors((x) => ({ ...x, confirm: "", form: "" }));
          }}
          error={Boolean(errors.confirm)}
          helperText={errors.confirm}
          sx={{ ...authField, mb: 2.25 }}
        />

        {errors.form && (
          <Box sx={{ mb: 1.5, font: `400 11.5px ${font.sans}`, color: color.danger }}>
            {errors.form}
          </Box>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{ py: 1.5, fontSize: 13.5 }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Update password →"}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ResetPwd;
